import { useSelector } from 'hooks/useSelector';
import React from 'react';
import {
  selectAseFieldInstanceAst,
  selectAseTplFieldInstanceCheck,
  selectAseTplFieldInstanceDefaultChecksForGroup,
} from 'store/assessments/selectors';
import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { Txt, TxtPreset } from 'components/Txt';
import { View } from 'react-native';
import { theme } from 'lib/theme';
import { addDays, addMonths, addQuarters, addYears } from 'date-fns';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { formatDate, numberToDate } from 'lib/riskbase';
import { ScreenSize, UI_DATE_FORMAT_SHORT } from 'lib/constants';
import {
  AseTplCheckPeriodIdEnum,
  FieldInstanceCheckScheduleStatusEnum,
} from 'store/assessments/types';
import { InstanceCheckAvailabilityBadge } from '../Parts/InstanceCheckAvailabilityBadge';
import { useScreenSize } from 'hooks/useScreenSize';

export const ExplorerInstanceCheckRowPreAdd: React.FC<{
  id: string;
  instanceId: string;
}> = ({ id, instanceId }) => {
  const screenSize = useScreenSize();
  const check = useSelector((state) =>
    selectAseTplFieldInstanceCheck(state, id),
  );
  const instance = useSelector((state) =>
    selectAseFieldInstanceAst(state, instanceId),
  );

  const actualCheckData = instance?.checks?.[id];
  const defaultCheckData = useSelector((state) =>
    selectAseTplFieldInstanceDefaultChecksForGroup(
      state,
      instance?.groupId || '-1',
    ),
  )[id];
  const checkData = actualCheckData || defaultCheckData;

  if (!check || !checkData) {
    return null;
  }

  const nowDate = new Date();
  const isComplete =
    checkData.schedule.statusId ===
    FieldInstanceCheckScheduleStatusEnum.completed;
  const checkTypeId = check.type.id;

  const lastAttemptedDate = checkData.lastAttempted
    ? numberToDate(checkData.lastAttempted.date)
    : null;
  const lastAttemptedDateLabel = lastAttemptedDate
    ? formatDate(lastAttemptedDate, UI_DATE_FORMAT_SHORT)
    : null;
  const lastCompletedDate = checkData.lastCompleted
    ? numberToDate(checkData.lastCompleted.date)
    : null;
  const lastCompletedDateLabel = lastCompletedDate
    ? formatDate(lastCompletedDate, UI_DATE_FORMAT_SHORT)
    : null;

  const showAttemptedAndCompleted =
    lastAttemptedDate &&
    lastCompletedDate &&
    lastAttemptedDate.getTime() > lastCompletedDate.getTime();

  const nextDueDate = lastCompletedDate
    ? checkTypeId === AseTplCheckPeriodIdEnum.year
      ? addYears(lastCompletedDate, 1)
      : checkTypeId === AseTplCheckPeriodIdEnum.month6
        ? addMonths(lastCompletedDate, 6)
        : checkTypeId === AseTplCheckPeriodIdEnum.quarter
          ? addQuarters(lastCompletedDate, 1)
          : checkTypeId === AseTplCheckPeriodIdEnum.month
            ? addMonths(lastCompletedDate, 1)
            : checkTypeId === AseTplCheckPeriodIdEnum.week
              ? addDays(lastCompletedDate, 7)
              : null
    : checkData.schedule.due?.to
      ? numberToDate(checkData.schedule.due.to)
      : null;
  const nextDueDateLabel = nextDueDate
    ? formatDate(nextDueDate, UI_DATE_FORMAT_SHORT)
    : null;
  const isOverdue = !!nextDueDate && nextDueDate.getTime() < nowDate.getTime();
  const duePill = isComplete
    ? {
        label: 'No Checks Due',
        iconColor: theme.colors.green,
      }
    : nextDueDateLabel
      ? {
          label: `${isOverdue ? 'Overdue' : 'Due by'} ${nextDueDateLabel}`,
          iconColor: isOverdue ? theme.colors.red : theme.colors.orange,
        }
      : null;

  const statusSummary = [
    duePill?.label || 'Not Required',
    showAttemptedAndCompleted
      ? [
          `Last Completed: ${lastCompletedDateLabel}`,
          `Last Attempted: ${lastAttemptedDateLabel}`,
        ].join(' • ')
      : `${lastCompletedDateLabel ? 'Last Completed: ' + lastCompletedDateLabel : 'Never Completed'}`,
  ].join(' • ');

  return (
    <>
      <SeparatorHorizontal />
      <CardRow
        style={{ minHeight: 0 }}
        icon={ICONS.CircleSolid}
        iconColor={duePill?.iconColor || theme.colors.fieldValueText}
        iconSize={12}
        childrenStyle={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.paddingHorizontal,
        }}
      >
        <View style={{ flex: 1 }}>
          <Txt weight="bold" preset={TxtPreset.CardRowLabel2}>
            {check.name}
          </Txt>
          <Txt preset={TxtPreset.CardRowLabel2}>{statusSummary}</Txt>
        </View>
        <InstanceCheckAvailabilityBadge
          availability={checkData?.availability || []}
          showIcon={screenSize === ScreenSize.large}
        />
      </CardRow>
    </>
  );
};
